/** YŪ SEI search: explicit queries only; Enter stays in the dialog.
 * Products/pages use the locale-aware Shopify predictive endpoint.
 * Active article entries come from the existing library and retain their IDs.
 * No history storage, Explore state, separate result page, or article-backdrop override.
 */
import { DialogComponent } from '@theme/dialog';

const normalize = (value) => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
const text = (node) => node?.textContent?.replace(/\s+/g, ' ').trim() || '';
const libraries = new Map();
function safeURL(value) {
  try {
    const url = new URL(value, location.origin);
    return url.origin === location.origin && /^https?:$/.test(url.protocol) ? url : null;
  } catch (_) { return null; }
}
function sameStoreURL(value) {
  const url = safeURL(value);
  if (!url) throw new Error('Search destination must be on this store.');
  const preview = new URL(location.href).searchParams.get('preview_theme_id');
  if (preview) url.searchParams.set('preview_theme_id', preview);
  return url;
}
async function getHTML(url, signal) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 12000);
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  try {
    if (signal?.aborted) controller.abort();
    const response = await fetch(sameStoreURL(url), { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('Search request failed.');
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  } finally { clearTimeout(timeout); signal?.removeEventListener('abort', abort); }
}
async function loadLibrary(url, force = false) {
  const key = sameStoreURL(url).href;
  const old = libraries.get(key);
  if (old?.promise) return old.promise;
  if (!force && old?.data && Date.now() - old.time < 30000) return old.data;
  const state = old || {};
  state.promise = (async () => {
    const rows = new Map(), visited = new Set();
    let next = key;
    while (next) {
      if (visited.has(next) || visited.size >= 100) throw new Error('Article pagination could not be completed.');
      visited.add(next);
      const doc = await getHTML(next);
      const grid = doc.querySelector('[data-yu-ritual-grid]');
      if (!grid) throw new Error('Article library was not found.');
      for (const card of grid.querySelectorAll('[data-yu-library-card]')) {
        const id = card.dataset.articleKey, title = text(card.querySelector('h2'));
        if (!id || !title) continue;
        const template = doc.getElementById(card.dataset.templateId || '');
        const target = sameStoreURL(url);
        target.searchParams.set('article', id); target.hash = 'articles';
        rows.set(id, {
          type: 'article', id, title, url: target.href,
          image: card.querySelector('img')?.getAttribute('src') || '',
          detail: [...card.querySelectorAll('.yu-ritual-card__meta span')].map(text).filter(Boolean).join(' · '),
          description: text(card.querySelector('.yu-ritual-card__description')),
          content: template instanceof HTMLTemplateElement ? text(template.content) : '',
          order: Number.isFinite(Number(card.dataset.yuOrder)) ? Number(card.dataset.yuOrder) : 999999,
        });
      }
      const nextHref = grid.querySelector('[data-yu-library-next] a')?.getAttribute('href');
      next = nextHref ? sameStoreURL(new URL(nextHref, next).href).href : null;
    }
    state.data = [...rows.values()].sort((a, b) => a.order - b.order);
    state.time = Date.now();
    return state.data;
  })();
  libraries.set(key, state);
  try { return await state.promise; } finally { state.promise = null; }
}
function matchingArticles(rows, query) {
  const q = normalize(query), terms = q.split(' ').filter(Boolean);
  if (!terms.length) return [];
  return rows.map((row) => {
    const title = normalize(row.title), body = normalize(`${row.description} ${row.detail} ${row.content}`);
    if (!terms.every((term) => title.includes(term) || body.includes(term))) return null;
    return { row, score: (title === q ? 1000 : title.startsWith(q) ? 100 : title.includes(q) ? 50 : 0) + terms.filter((term) => title.includes(term)).length * 10 };
  }).filter(Boolean).sort((a, b) => b.score - a.score || a.row.order - b.row.order).map((entry) => entry.row);
}
function liveRows(value, type) {
  return Array.isArray(value) ? value.filter((row) => {
    const url = safeURL(row?.url);
    return row?.type === type && typeof row.title === 'string' && url &&
      (type === 'product' ? /\/products\/[^/]+\/?$/.test(url.pathname) : /\/pages\/[^/]+\/?$/.test(url.pathname));
  }) : [];
}
function rowElement(item) {
  const li = document.createElement('li'); li.className = 'yu-spotlight__result';
  const link = document.createElement('a'); link.className = 'yu-spotlight__row'; link.href = sameStoreURL(item.url).href;
  const media = document.createElement('span'); media.className = 'yu-spotlight__thumb';
  if (item.image) {
    try {
      const imageURL = new URL(item.image, location.href);
      if (/^https?:$/.test(imageURL.protocol)) {
        const image = document.createElement('img'); image.src = imageURL.href; image.alt = ''; image.width = 64; image.height = 64; image.loading = 'lazy'; media.append(image);
      }
    } catch (_) {}
  }
  if (!media.children.length) { media.textContent = '↗'; media.setAttribute('aria-hidden', 'true'); }
  const copy = document.createElement('span'); copy.className = 'yu-spotlight__copy';
  const title = document.createElement('span'); title.className = 'yu-spotlight__title'; title.textContent = item.title;
  const detail = document.createElement('span'); detail.className = 'yu-spotlight__detail';
  const kind = item.type === 'product' ? 'Product' : item.type === 'article' ? 'Article' : 'Page';
  detail.textContent = [item.detail, item.detail === kind ? '' : kind, item.type === 'product' && item.available === false ? 'Sold out' : ''].filter(Boolean).join(' · ');
  copy.append(title, detail);
  const arrow = document.createElement('span'); arrow.className = 'yu-spotlight__arrow'; arrow.textContent = '→'; arrow.setAttribute('aria-hidden', 'true');
  link.append(media, copy, arrow); li.append(link); return li;
}
class SpotlightDialog extends DialogComponent {
  async showDialog() {
    if (this.opening || this.refs.dialog.open) return;
    this.opening = true;
    try {
      await Promise.all([...document.querySelectorAll('theme-drawer[open]')].map((drawer) => typeof drawer.close === 'function' ? drawer.close() : Promise.resolve()));
      super.showDialog();
    } finally { this.opening = false; }
  }
}
class Spotlight extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const listen = (node, type, fn) => node?.addEventListener(type, fn, { signal: this.controller.signal });
    this.host = this.closest('#search-modal'); this.dialog = this.closest('dialog');
    this.input = this.querySelector('input[name="q"]'); this.list = this.querySelector('.yu-spotlight__results');
    this.status = this.querySelector('[data-yu-search-status]'); this.body = this.querySelector('[data-yu-search-body]');
    this.version = 0; this.composing = false;
    if (!this.input || !this.list || !this.status || !this.body) return;
    listen(this.host, 'dialog:open', () => this.open());
    listen(this.host, 'dialog:close', () => this.reset());
    listen(this.input, 'input', () => { if (!this.composing) this.schedule(); });
    listen(this.input, 'search', () => this.schedule(true));
    listen(this.input, 'compositionstart', () => { this.composing = true; this.invalidate(); });
    listen(this.input, 'compositionend', () => { this.composing = false; this.schedule(); });
    listen(this.querySelector('form'), 'submit', (event) => {
      // Always stop the native /search navigation, including Enter with no result selected.
      event.preventDefault(); event.stopPropagation();
      if (!this.composing) this.schedule(true);
    });
    listen(this, 'keydown', (event) => this.keydown(event));
    listen(this.list, 'click', async (event) => {
      const link = event.target instanceof Element ? event.target.closest('a.yu-spotlight__row') : null;
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const destination = sameStoreURL(link.href);
      event.preventDefault(); event.stopPropagation();
      await this.host.closeDialog();
      window.location.assign(destination.href);
    });
    listen(document, 'keydown', (event) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey || event.key.toLowerCase() !== 'k' || event.isComposing) return;
      if (event.target instanceof Element && event.target.closest('input,textarea,select,[contenteditable="true"]') && event.target !== this.input) return;
      event.preventDefault(); this.dialog.open ? this.host.closeDialog() : this.host.showDialog();
    });
  }
  disconnectedCallback() { this.invalidate(); this.controller?.abort(); this.controller = null; }
  invalidate() { this.version++; clearTimeout(this.timer); this.request?.abort(); this.request = null; }
  reset() {
    this.invalidate(); this.composing = false; this.input.value = '';
    this.list.replaceChildren(); this.status.textContent = ''; this.body.hidden = true;
    this.list.removeAttribute('aria-busy');
  }
  open() {
    this.reset(); this.input.focus({ preventScroll: true });
    // Refresh active article identities without rendering an initial suggestion state.
    loadLibrary(this.dataset.libraryUrl, true).catch(() => {});
  }
  schedule(immediate = false) {
    this.invalidate();
    const query = this.input.value.trim();
    this.list.replaceChildren(); this.body.hidden = !query; this.body.scrollTop = 0;
    if (!query) { this.status.textContent = ''; this.list.removeAttribute('aria-busy'); return; }
    this.status.textContent = 'Searching…'; this.list.setAttribute('aria-busy', 'true');
    const version = this.version;
    if (immediate) this.search(query, version);
    else this.timer = setTimeout(() => this.search(query, version), 160);
  }
  async search(query, version) {
    this.request = new AbortController();
    const url = sameStoreURL(this.dataset.suggestUrl);
    url.searchParams.set('q', query); url.searchParams.set('section_id', 'yu-spotlight-results');
    url.searchParams.set('resources[type]', 'product,page'); url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each'); url.searchParams.set('resources[options][unavailable_products]', 'last');
    const productsAndPages = getHTML(url, this.request.signal).then((doc) => {
      const raw = doc.querySelector('[data-yu-spotlight-suggestions]');
      if (!raw) throw new Error('Suggestions not available.');
      return JSON.parse(raw.textContent);
    });
    const articles = loadLibrary(this.dataset.libraryUrl).then((rows) => matchingArticles(rows, query));
    const [native, editorial] = await Promise.allSettled([productsAndPages, articles]);
    if (!this.isConnected || version !== this.version || !this.dialog.open || this.input.value.trim() !== query) return;
    const data = native.status === 'fulfilled' ? native.value : {};
    const rows = [...liveRows(data.products, 'product'), ...(editorial.status === 'fulfilled' ? editorial.value : []), ...liveRows(data.pages, 'page')];
    const seen = new Set();
    const distinct = rows.filter((row) => { const href = safeURL(row.url)?.href; if (!href || seen.has(href)) return false; seen.add(href); return true; });
    this.list.replaceChildren(...distinct.map(rowElement)); this.list.removeAttribute('aria-busy');
    const partial = native.status === 'rejected' || editorial.status === 'rejected';
    this.status.textContent = partial ? (distinct.length ? 'Some results could not load. You can try searching again.' : 'Search is temporarily unavailable. Please try again.')
      : distinct.length ? `${distinct.length} ${distinct.length === 1 ? 'result' : 'results'}` : 'No matching results. Try a different word.';
  }
  keydown(event) {
    if (event.isComposing || this.composing) { event.stopPropagation(); return; }
    if (event.target === this.input && event.key === 'Enter') {
      // Input Enter searches. Enter on a deliberately focused result link opens that result.
      event.preventDefault(); event.stopPropagation(); this.schedule(true); return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const links = [...this.list.querySelectorAll('a')];
    if (!links.length || (event.target !== this.input && !links.includes(event.target))) return;
    event.preventDefault(); event.stopPropagation();
    const index = links.indexOf(event.target);
    const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
    if (event.target === this.input && event.key === 'ArrowUp') links.at(-1).focus({ preventScroll: true });
    else if (next < 0 || next >= links.length) this.input.focus({ preventScroll: true });
    else links[next].focus({ preventScroll: true });
    const focused = document.activeElement;
    if (links.includes(focused)) {
      const a = focused.getBoundingClientRect(), b = this.body.getBoundingClientRect();
      if (a.bottom > b.bottom) this.body.scrollTop += a.bottom - b.bottom;
      else if (a.top < b.top) this.body.scrollTop -= b.top - a.top;
    }
  }
}
if (!customElements.get('yu-spotlight-dialog-component')) customElements.define('yu-spotlight-dialog-component', SpotlightDialog);
if (!customElements.get('yu-spotlight')) customElements.define('yu-spotlight', Spotlight);
